import React, { Component } from 'react';
import { PropTypes } from 'prop-types';
import { connect } from 'react-redux';
import { reduxForm, Field } from 'redux-form';
import { toastr } from 'react-redux-toastr';

import { ApiService } from 'services';
import { addSupportingDoc, deleteSupportingDoc, getStorageSetting } from 'actions';

import AccessControl from 'components/AccessControl';
import ActivityPerms from 'components/AccessControl/permissions.json';

import FileUpload from 'components/Common/FileUpload';
import EvidentiaryDocs from 'components/EvidentiaryDocs';
import FormWorkflowStages from 'common/Workflows/FormWorkflowStages';

class SupportingDocsSection extends Component {
  static propTypes = {
    dispatch: PropTypes.func.isRequired,
    permissions: PropTypes.arrayOf(PropTypes.string),
    workflowStage: PropTypes.number,
    workflowUsername: PropTypes.string,
    formId: PropTypes.number,
    isDeletingSupportingDocId: PropTypes.number,
    supportingDocError: PropTypes.shape({}),
    documents: PropTypes.arrayOf(PropTypes.shape()),
    isLocked: PropTypes.bool,
    collectableSupportingDocs: PropTypes.arrayOf(PropTypes.shape()),
  };

  static defaultProps = {
    permissions: null,
    workflowStage: 0,
    workflowUsername: null,
    formId: null,
    documents: [],
    isDeletingSupportingDocId: null,
    supportingDocError: null,
    isLocked: false,
    collectableSupportingDocs: null
  };

  constructor(props) {
    super(props);

    const files = getStorageSetting('files') || [];

    this.state = {
      supportingDocType: '',
      files
    };
  }

  static onFileUploadCancel(file) {
    toastr.info('', `Upload of ${ file.name } has been canceled.`);
  }

  static onFileUploadError(file, error) {
    toastr.error('', `Upload of ${ file.name } has been canceled.  ${ error.message }`);
  }

  static onViewSupportingDocument(file) {
    if (file) {
      window.open(`customers/supporting-docs/${ file.id }`, '_blank'); // to open new page
      return;
    }
    toastr.error('', 'Sorry, this document cannot be viewed');
  }

  onFileUploadSuccess(file, response) {
    const { dispatch } = this.props;
    dispatch(addSupportingDoc(response));
    toastr.success('', `${ response[0].originalFileName } has been uploaded`);
  }

  onDeleteSupportingDocument(file) {
    const { dispatch } = this.props;
    dispatch(deleteSupportingDoc(file.id));
  }

  onSupportingDocTypeChange = (e, newValue) => this.setState({ supportingDocType: newValue });

  groupDocuments = () => {
    const { documents } = this.props;
    const map = new Map();

    if (documents != null) {
      documents.forEach((doc) => {
        if (doc != null) {
          const key = doc.typeDescription;
          const collection = map.get(key);
          if (collection == null) {
            map.set(key, [doc]);
          } else {
            collection.push(doc);
          }
        }
      });
    }

    return Array.from(map);
  };

  canUpload() {
    const { permissions, workflowStage, workflowUsername, isLocked } = this.props;

    const isUserFormOwner = workflowUsername != null && workflowUsername === getStorageSetting('username');
    const hasReviewerPermission = AccessControl.hasPermission(permissions, [ActivityPerms.fwfInitialReviewer, ActivityPerms.fwfSecondaryReviewer, ActivityPerms.fwfExpertReviewer, ActivityPerms.fwfSupplReviewer]);

    if (workflowStage === FormWorkflowStages.Complete || workflowStage === FormWorkflowStages.BatchStaging || isLocked) {
      // WF is complete or batch staging or form is locked
      return false;
    }

    return isUserFormOwner && hasReviewerPermission;
  }

  render() {
    const { permissions, formId, isDeletingSupportingDocId, collectableSupportingDocs } = this.props;
    const { files, supportingDocType } = this.state;
    const groupedDocs = this.groupDocuments();

    return (
      <div className="supporting-docs">
        <h1>Supporting Documents</h1>
        <div className="supporting-docs-content">
          <AccessControl permissions={ permissions } allowedPermissions={ [ActivityPerms.scannedSupDocs] }>
            { this.canUpload() && <form id="supportingDocForm" className="form-horizontal side-bar-form">
              <div>
                <div className="form-group">
                  <div className="col-4 col-sm-12">
                    <label htmlFor="supporting-doc-type" className="form-label">Document Type</label>
                  </div>
                  <div className="col-8 col-sm-12">
                    <Field
                      id="supporting-doc-type"
                      name="supportingDocType"
                      component={ EvidentiaryDocs }
                      className="form-select"
                      hideHints
                      value={ supportingDocType }
                      onChange={ (e, newValue) => this.onSupportingDocTypeChange(e, newValue) }
                      showCollectableSupportingDocs
                      collectableSupportingDocs={ collectableSupportingDocs }
                    />
                  </div>
                </div>
                <FileUpload
                  defaultMessage="Drop files here to upload in bmp, gif, jpg, png, tif, doc, docx, xls, xlsx, msg or pdf format"
                  postUrl={ ApiService.getFileUploadUrl() }
                  accessTokenName={ ApiService.getAccessTokenName() }
                  acceptedFiles="application/pdf,.jpg,.png,.bmp,.msg,.doc,.docx,.jpeg,.tiff,.gif,.tif,.xls,.xlsx"
                  maxFilesize={ files.maxFilesize }
                  timeout={ files.uploadTimeout }
                  docCategory="supportingdocument"
                  docType={ supportingDocType != null ? supportingDocType : '' }
                  id={ formId }
                  removeFileAfterUpload
                  onFileUploadCancel={ file => SupportingDocsSection.onFileUploadCancel(file) }
                  onFileUploadSuccess={ (file, response) => this.onFileUploadSuccess(file, response) }
                  onFileUploadError={ (file, error) => SupportingDocsSection.onFileUploadError(file, error) }
                />
              </div>
            </form>}
          </AccessControl>
          <div className={ `form-group side-bar-section ${ !this.canUpload() ? 'center' : '' }` }>
            <div className="col-12">
              { (groupedDocs == null || groupedDocs.length === 0) && <table className="table table-striped table-hover mb">
                <thead>
                  <tr><th colSpan="2" className="no-rows">None uploaded.</th></tr>
                </thead>
              </table> }
              { groupedDocs != null && groupedDocs.map(group => group != null && <table key={ group[0] } className="table table-striped table-hover mb">
                <thead>
                  <tr><th colSpan="2">{ group[0] } Document(s)</th></tr>
                </thead>
                <tbody>
                  { group[1] != null && group[1].map(doc => doc != null && <tr key={ doc.id }>
                    <td className="col-10">{ `${ doc.originalFileName }` }</td>
                    <td className="col-2 actions">
                      <AccessControl permissions={ permissions } allowedPermissions={ [ActivityPerms.scannedDownload] }>
                        <button
                          className="btn btn-sm"
                          type="button"
                          title="View document"
                          disabled={ isDeletingSupportingDocId === doc.id }
                          onClick={ () => SupportingDocsSection.onViewSupportingDocument(doc) }
                        >
                          <span className="icon icon-share" />
                        </button>
                      </AccessControl>
                      <AccessControl permissions={ permissions } allowedPermissions={ [ActivityPerms.scannedSupDocs] }>
                        <button
                          className={ `btn btn-sm btn-danger ${ !this.canUpload() ? 'none' : '' }` }
                          type="button"
                          title="Delete document"
                          disabled={ isDeletingSupportingDocId === doc.id }
                          onClick={ () => this.onDeleteSupportingDocument(doc) }
                        >
                          <span className="icon icon-delete" />
                        </button>
                      </AccessControl>
                    </td>
                  </tr>)}
                </tbody>
              </table>)}
            </div>
          </div>
        </div>
      </div>
    );
  }
}

const supportingDocForm = reduxForm({
  form: 'supportingDocForm',
})(SupportingDocsSection);

function mapStateToProps(state) {
  const {
    auth: {
      permissions
    },
    forms: {
      isDeletingSupportingDocId,
      supportingDocError,
      formByIdResponse: {
        collectableSupportingDocs
      }
    }
  } = state;

  return {
    permissions,
    isDeletingSupportingDocId,
    supportingDocError,
    collectableSupportingDocs
  };
}

const mapDispatchToProps = dispatch => ({ dispatch });

export const SupportingDocs = connect(
  mapStateToProps,
  mapDispatchToProps
)(supportingDocForm);

export default SupportingDocs;
